"use client"

import { useState, useMemo, useEffect } from "react"
import { useRouter } from "next/navigation"
import { HttpTypes } from "@medusajs/types"
import { clx, Select } from "@medusajs/ui"
import { useExpressCart } from "@/modules/express-checkout/providers/express-cart"
import { addToExpressCart as addToExpressCartAction } from "@/lib/data/express-cart"
import Card from "../card"
import Button from "@/modules/common/components/button"
import { getProductPrice } from "@/lib/util/get-product-price"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import Thumbnail from "@/modules/products/components/thumbnail"
import { convertToLocale } from "@/lib/util/money"
import { CART_DRAWER_CLOSED } from "@/lib/data/cart-event-bus"
import { toast } from "@medusajs/ui"

type ExpressProductProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  countryCode: string
  isActive: boolean
  isCompleted: boolean
  isCartValid?: boolean
}

/**
 * Product step in express checkout
 * Allows variant selection and adding to cart
 */
const ExpressProduct = ({
  product,
  region,
  countryCode,
  isActive,
  isCompleted,
  isCartValid = false,
}: ExpressProductProps) => {
  const router = useRouter()
  const { cart, createExpressCart, addToExpressCart, isLoading, updateItemQuantity, refreshCart, setCartDirect } =
    useExpressCart()

  const [selectedVariantId, setSelectedVariantId] = useState<string>(
    product.variants?.[0]?.id || ""
  )
  const [quantity, setQuantity] = useState(1)

  // Refresh express cart when cart drawer closes (user may have removed items from cart drawer)
  useEffect(() => {
    const handler = () => {
      refreshCart()
    }
    window.addEventListener(CART_DRAWER_CLOSED, handler)
    return () => window.removeEventListener(CART_DRAWER_CLOSED, handler)
  }, [refreshCart])

  // Get selected variant
  const selectedVariant = useMemo(() => {
    return product.variants?.find((v) => v.id === selectedVariantId)
  }, [product.variants, selectedVariantId])

  // Get price for selected variant
  const { variantPrice } = getProductPrice({
    product,
    variantId: selectedVariantId,
  })

  // Check if product has multiple variants with actual options
  const hasMultipleVariants = useMemo(() => {
    if (!product.variants || product.variants.length <= 1) return false
    // Check if variants have meaningful options (not just "Default option value")
    return product.variants.some((v) =>
      v.options?.some((o) => o.value !== "Default option value")
    )
  }, [product.variants])

  // Handle add to cart and proceed
  const handleContinue = async () => {
    if (!selectedVariantId) return

    let currentCart = cart

    // Create cart if it doesn't exist
    if (!currentCart) {
      currentCart = await createExpressCart(region.id)
      if (!currentCart) return
    }

    // Check if this variant is already in the cart
    const existingItem = currentCart.items?.find(
      (item) => item.variant_id === selectedVariantId
    )

    if (existingItem) {
      await updateItemQuantity(existingItem.id, quantity)
    } else {
      // Clear cart items first if there's a different product
      const itemsToClear = currentCart.items?.filter(
        (item) => item.product_id !== product.id
      ) || []

      for (const item of itemsToClear) {
        await updateItemQuantity(item.id, 0)
      }

      try {
        const updatedCart = await addToExpressCartAction(currentCart.id, selectedVariantId, quantity)
        currentCart = updatedCart
        setCartDirect(updatedCart)
      } catch {
        toast.error("Napaka pri dodajanju v košarico")
        return
      }
    }

    router.push(`/${countryCode}/products/${product.handle}?express=true&step=address`)
  }

  // Handle edit - go back to product step
  const handleEdit = () => {
    router.push(`/${countryCode}/products/${product.handle}?express=true&step=product`)
  }

  // Get cart item for this product if it exists
  const cartItem = cart?.items?.find((item) => item.product_id === product.id)

  // When cart has items matching this product, show cart summary (user came from cart drawer or has multiple)
  const showCartSummary = isActive && isCartValid && (cart?.items?.length ?? 0) > 0

  // Simple continue when cart already has items - just navigate
  const handleContinueFromCart = () => {
    router.push(`/${countryCode}/products/${product.handle}?express=true&step=address`)
  }

  return (
    <Card
      title="1. Izdelek"
      isActive={isActive}
      isCompleted={isCompleted}
      onEdit={handleEdit}
    >
      {showCartSummary ? (
        <div className="flex flex-col gap-6">
          <h4 className="font-medium text-neutral-900">Izdelki v košarici</h4>
          {cart?.items?.map((item) => (
            <div key={item.id} className="flex items-center gap-4">
              <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-neutral-100">
                <Thumbnail
                  thumbnail={item.thumbnail}
                  alt={item.title || "Cart item"}
                  size="square"
                />
              </div>
              <div className="flex-1 flex flex-col">
                <span className="font-medium text-sm">{item.title}</span>
                <span className="text-sm text-neutral-500">
                  Količina:{" "}
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => {
                      const newQty = parseInt(e.target.value) || 0
                      if (newQty >= 0) {
                        updateItemQuantity(item.id, newQty)
                      }
                    }}
                    className="w-12 px-2 py-1 border border-neutral-200 rounded text-center"
                    min={0}
                  />
                </span>
              </div>
              <span className="font-medium">
                {convertToLocale({
                  amount: item.total ?? (item.unit_price ?? 0) * (item.quantity ?? 0),
                  currency_code: cart?.currency_code || "EUR",
                })}
              </span>
            </div>
          ))}
          <Button
            onClick={handleContinueFromCart}
            isLoading={isLoading}
            disabled={!cart?.items?.length}
            className="w-full"
            size="large"
          >
            Nadaljuj na dostavo
          </Button>
          <div className="text-center">
            <LocalizedClientLink
              href={`/products/${product.handle}`}
              className="text-sm text-neutral-500 hover:text-neutral-700 underline"
            >
              Poglej podrobnosti izdelka
            </LocalizedClientLink>
          </div>
        </div>
      ) : isActive ? (
        <div className="flex flex-col gap-6">
          {/* Product Info */}
          <div className="flex gap-4">
            <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-neutral-100">
              <Thumbnail
                thumbnail={product.thumbnail}
                images={product.images}
                alt={product.title || product.handle || "Product image"}
                size="square"
              />
            </div>
            <div className="flex flex-col justify-center">
              <h3 className="font-semibold text-lg">{product.title}</h3>
              {selectedVariant && variantPrice && (
                <p className="text-neutral-600 text-lg font-medium">
                  {variantPrice.calculated_price}
                </p>
              )}
            </div>
          </div>

          {/* Variant Selection */}
          {hasMultipleVariants && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">
                Izberite različico
              </label>
              <Select
                value={selectedVariantId}
                onValueChange={setSelectedVariantId}
              >
                <Select.Trigger>
                  <Select.Value placeholder="Izberite različico" />
                </Select.Trigger>
                <Select.Content>
                  {product.variants?.map((variant) => {
                    const optionLabel = variant.options
                      ?.filter((o) => o.value !== "Default option value")
                      .map((o) => o.value)
                      .join(" / ")

                    return (
                      <Select.Item key={variant.id} value={variant.id}>
                        {optionLabel || variant.title || `Varianta ${variant.id}`}
                      </Select.Item>
                    )
                  })}
                </Select.Content>
              </Select>
            </div>
          )}

          {/* Quantity Selection */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral-700">
              Količina
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-lg font-medium transition-colors"
                disabled={quantity <= 1}
              >
                -
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                }
                className="w-16 h-10 text-center border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                min={1}
              />
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-lg font-medium transition-colors"
              >
                +
              </button>
            </div>
          </div>

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            isLoading={isLoading}
            disabled={!selectedVariantId}
            className="w-full"
            size="large"
          >
            Nadaljuj na dostavo
          </Button>

          {/* Link to regular product page */}
          <div className="text-center">
            <LocalizedClientLink
              href={`/products/${product.handle}`}
              className="text-sm text-neutral-500 hover:text-neutral-700 underline"
            >
              Poglej podrobnosti izdelka
            </LocalizedClientLink>
          </div>
        </div>
      ) : isCompleted && cart?.items?.length ? (
        // Collapsed view when completed
        <div className="flex flex-col gap-2 pt-2">
          {cart.items.length === 1 && cartItem ? (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-neutral-100">
                <Thumbnail
                  thumbnail={product.thumbnail}
                  images={product.images}
                  alt={product.title || product.handle || "Product image"}
                  size="square"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-medium">{product.title}</span>
                <span className="text-sm text-neutral-600">
                  Količina: {cartItem.quantity}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {cart.items.map((item) => (
                <div key={item.id} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{item.title}</span>
                  <span className="text-neutral-600">× {item.quantity}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </Card>
  )
}

export default ExpressProduct






